import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../../lib/supabaseClient";

const FILTERS = ["all", "pending", "seen", "answered", "quoted", "accepted", "rejected", "done"];

const badgeClass = (status) => ({
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  seen: "bg-blue-100 text-blue-800 border-blue-200",
  answered: "bg-emerald-100 text-emerald-800 border-emerald-200",
  quoted: "bg-purple-100 text-purple-800 border-purple-200",
  accepted: "bg-green-100 text-green-800 border-green-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
  done: "bg-gray-100 text-gray-700 border-gray-200",
}[status] || "bg-gray-100 text-gray-700 border-gray-200");

const normStatus = (status) => String(status || "pending").toLowerCase().trim();

function requestSummary(request) {
  const payload = request?.payload || {};
  const artwork = payload.artwork || {};
  const client = payload.client || {};
  return {
    title: artwork.title || payload.artwork_title,
    artist: artwork.artist || payload.artwork_artist,
    clientName: client.name || payload.client_name,
    insuredValue: request?.insured_value ?? payload.insured_value ?? payload.value,
    currency: request?.currency || payload.currency || "EUR",
    context: payload.context || payload.message,
  };
}

export default function PartnerInsuranceDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [requests, setRequests] = useState([]);
  const [partnership, setPartnership] = useState(null);
  const [error, setError] = useState("");

  const formatDate = (value) => {
    if (!value) return "—";
    try {
      return new Intl.DateTimeFormat(i18n.language || "fr", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
    } catch { return String(value); }
  };

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const { data: auth, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      if (!auth?.user?.id) throw new Error(t("partner.common.noSession"));

      const [requestResult, partnershipResult] = await Promise.all([
        supabase.from("insurance_requests")
          .select("id,created_at,updated_at,status,request_type,payload,insured_value,currency,notes,seen_at,answered_at,closed_at")
          .eq("partner_id", auth.user.id)
          .order("created_at", { ascending: false }),
        supabase.from("global_partners")
          .select("relationship_type,status,access_mode,starts_at,ends_at,renewal_mode")
          .eq("user_id", auth.user.id)
          .maybeSingle(),
      ]);
      if (requestResult.error) throw requestResult.error;
      setRequests((requestResult.data || []).map((item) => ({ ...item, status: normStatus(item.status) })));
      if (!partnershipResult.error) setPartnership(partnershipResult.data || null);
    } catch (e) {
      console.error("[PartnerInsuranceDashboard]", e);
      setError(e?.message || t("partner.dashboard.loadError"));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(
    () => filter === "all" ? requests : requests.filter((item) => item.status === filter),
    [filter, requests]
  );
  const stats = useMemo(() => ({
    total: requests.length,
    pending: requests.filter((r) => r.status === "pending").length,
    quoted: requests.filter((r) => ["answered", "quoted"].includes(r.status)).length,
    accepted: requests.filter((r) => ["accepted", "done"].includes(r.status)).length,
  }), [requests]);

  const markSeen = async (id) => {
    const current = requests.find((item) => item.id === id);
    if (current?.status !== "pending") return;
    const { error: updateError } = await supabase.from("insurance_requests")
      .update({ status: "seen", seen_at: current.seen_at || new Date().toISOString() }).eq("id", id);
    if (updateError) setError(t("partner.dashboard.seenError")); else load();
  };

  const metricCards = [
    ["total", stats.total, "📥"], ["pending", stats.pending, "⏳"],
    ["quoted", stats.quoted, "📄"], ["accepted", stats.accepted, "🤝"],
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-5 py-8 space-y-6">
        <section className="rounded-2xl border border-sky-200 bg-gradient-to-br from-sky-50 to-white p-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-sky-700">{t("partner.insuranceDashboard.eyebrow")}</p>
          <h1 className="mt-2 text-3xl font-serif font-semibold text-gray-950">🛡️ {t("partner.insurance.title")}</h1>
          <p className="mt-2 max-w-3xl text-gray-650">{t("partner.insuranceDashboard.intro")}</p>
        </section>

        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {metricCards.map(([key, value, icon]) => (
            <div key={key} className="rounded-2xl border border-gray-200 bg-white p-4">
              <div className="flex items-center justify-between"><span className="text-sm text-gray-600">{t(`partner.insuranceDashboard.metrics.${key}`)}</span><span>{icon}</span></div>
              <div className="mt-2 text-3xl font-semibold">{value}</div>
            </div>
          ))}
        </section>

        <section className="grid lg:grid-cols-[1.5fr_1fr] gap-4">
          <div className="rounded-2xl border border-gray-200 bg-white p-5">
            <h2 className="font-serif text-xl">{t("partner.insuranceDashboard.workflowTitle")}</h2>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              {["review", "qualify", "quote", "follow"].map((step, index) => (
                <div key={step} className="rounded-xl bg-gray-50 p-4">
                  <span className="text-xs font-semibold text-sky-700">0{index + 1}</span>
                  <p className="mt-1 text-sm font-medium">{t(`partner.insuranceDashboard.workflow.${step}`)}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
            <h2 className="font-serif text-xl">{t("partner.insuranceDashboard.partnershipTitle")}</h2>
            {partnership ? (
              <dl className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between gap-4"><dt className="text-gray-600">{t("partner.dashboard.partnershipStatus")}</dt><dd className="font-medium">{t(`partner.dashboard.partnerStatus.${partnership.status}`, { defaultValue: partnership.status })}</dd></div>
                <div className="flex justify-between gap-4"><dt className="text-gray-600">{t("partner.dashboard.relationship")}</dt><dd className="text-right font-medium">{t(`partner.dashboard.relationships.${partnership.relationship_type}`, { defaultValue: partnership.relationship_type })}</dd></div>
                <div className="flex justify-between gap-4"><dt className="text-gray-600">{t("partner.dashboard.validUntil")}</dt><dd className="font-medium">{partnership.ends_at ? formatDate(partnership.ends_at).split(",")[0] : t("partner.dashboard.noExpiry")}</dd></div>
              </dl>
            ) : <p className="mt-3 text-sm text-gray-700">{t("partner.dashboard.noPartnershipRecord")}</p>}
            <p className="mt-4 border-t border-amber-200 pt-4 text-xs text-gray-650">{t("partner.insuranceDashboard.commercialTerms")}</p>
          </div>
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
          <div className="p-5 border-b border-gray-200">
            <div className="flex flex-wrap gap-2">
              {FILTERS.map((key) => <button key={key} onClick={() => setFilter(key)} className={`px-3 py-2 rounded-xl text-sm font-medium ${filter === key ? "bg-black text-white" : "bg-gray-50 border border-gray-200"}`}>{t(`partner.filters.${key}`)}</button>)}
              <button onClick={load} className="ml-auto px-3 py-2 rounded-xl text-sm border border-gray-200">{t("partner.common.refresh")}</button>
            </div>
            <p className="mt-3 text-xs text-gray-500">🔒 {t("partner.insuranceDashboard.privacyBody")}</p>
          </div>

          {error && <div className="m-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}
          {loading ? <div className="p-12 text-center text-gray-500">{t("partner.common.loading")}</div> : filtered.length === 0 ? (
            <div className="p-12 text-center"><div className="text-4xl">📭</div><h3 className="mt-3 font-serif text-xl">{t("partner.insuranceDashboard.emptyTitle")}</h3><p className="mt-2 text-sm text-gray-500">{filter === "all" || filter === "pending" ? t("partner.insuranceDashboard.emptyBody") : t("partner.emptyState.filtered")}</p></div>
          ) : <div className="divide-y divide-gray-100">{filtered.map((request) => {
            const summary = requestSummary(request);
            return <article key={request.id} className="p-5 hover:bg-gray-50">
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                <div className="min-w-0"><div className="text-xs text-gray-500">{formatDate(request.created_at)}</div><h3 className="mt-2 font-semibold">{summary.title || t("partner.common.artwork")} {summary.artist && <span className="font-normal text-gray-500">• {summary.artist}</span>}</h3>
                  <div className="mt-2 grid sm:grid-cols-3 gap-x-6 gap-y-1 text-sm text-gray-600"><span>{t("partner.common.client")}: {summary.clientName || "—"}</span><span>{t("partner.common.type")}: {request.request_type || "insurance_quote"}</span><span>{t("partner.common.value")}: {summary.insuredValue != null ? `${summary.insuredValue} ${summary.currency}` : "—"}</span></div>
                  {summary.context && <p className="mt-2 text-sm text-gray-600 line-clamp-2">{summary.context}</p>}
                </div>
                <div className="flex md:flex-col items-center md:items-end gap-2"><span className={`text-xs px-2 py-1 rounded-lg border ${badgeClass(request.status)}`}>{t(`partner.status.${request.status}`, { defaultValue: request.status })}</span>{request.status === "pending" && <button onClick={() => markSeen(request.id)} className="px-3 py-2 rounded-xl text-sm bg-gray-100">{t("partner.dashboard.markSeen")}</button>}<button onClick={() => navigate(`/partner/insurance/requests/${request.id}`)} className="px-3 py-2 rounded-xl text-sm bg-black text-white">{t("partner.requests.viewDetails")}</button></div>
              </div>
            </article>;
          })}</div>}
        </section>
      </main>
    </div>
  );
}
